import { AsyncPipe, KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { saveAs } from 'file-saver';
import { Ipv4, isElectronContext, MotionMasterConnectionConfig } from 'motion-master-client';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { generateClientId } from '@app/models/connection-config';
import { NetworkInterface, SpoePdoMode } from '@app/models/network-interface';
import { BrandService } from '@app/services/brand.service';
import { ConnectionService } from '@app/services/connection.service';
import { ElectronService } from '@app/services/electron.service';
import { GlobalService } from '@app/services/global.service';
import { SettingService } from '@app/services/setting.service';
import { FormIpv4Component } from '@app/shared/form-ipv4/form-ipv4.component';
import { isElectronDirective } from '@app/shared/is-electron.directive';
import { LoadEsiButtonComponent } from '@app/shared/load-esi-button/load-esi-button.component';
import { MainStatusbarComponent } from '@app/shared/main-statusbar/main-statusbar.component';
import { RadioButtonLayoutComponent } from '@app/shared/radio-button-layout/radio-button-layout.component';
import { RadioButtonLayoutOptionDirective } from '@app/shared/radio-button-layout/radio-button-layout.directive';
import { SwitchBackButtonComponent } from '@app/shared/switch-back-button/switch-back-button.component';
import { UpdateSoftwareButtonComponent } from '@app/shared/update-software-button.component';
import { SystemInformation } from '@app/store/global.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  imports: [
    RouterLink,
    SwitchBackButtonComponent,
    LoadEsiButtonComponent,
    UpdateSoftwareButtonComponent,
    isElectronDirective,
    FormsModule,
    ReactiveFormsModule,
    FormIpv4Component,
    NgTemplateOutlet,
    MainStatusbarComponent,
    AsyncPipe,
    RadioButtonLayoutComponent,
    RadioButtonLayoutOptionDirective,
    KeyValuePipe,
  ],
})
export class StartComponent implements OnInit {
  private cn = inject(ConnectionService);
  private brandService = inject(BrandService);
  private electronService = inject(ElectronService);
  private fb = inject(FormBuilder);
  private settingService = inject(SettingService);
  private globalService = inject(GlobalService);
  private toastr = inject(ToastrService);

  logoUrl$ = this.brandService.logoUrl$;
  brandName$ = this.brandService.brandName$;
  isOblacDrivesBrand$ = this.brandService.isOblacDrivesBrand$;

  expertMode$ = this.settingService.expertMode$;

  motionMasterRunning$ = this.electronService.motionMasterRunning$;

  onceMotionMasterIsStarted$ = this.motionMasterRunning$.pipe(filter(Boolean), first());
  isConnectedWithExpertMode$ = this.globalService.isConnectedWithExpertMode$;

  isElectronApp = false;
  platform?: string;
  isPortInUse = false;
  isNpcapInstalled = false;

  startForm = this.fb.group({
    fieldBus: this.fb.control(null, { validators: [Validators.required] }),
    spoeCheckbox: this.fb.control(false),
    reqResPort: this.fb.control(null, { validators: [Validators.required] }),
    pubSubPort: this.fb.control(null, { validators: [Validators.required] }),
    driver: this.fb.control(null, { validators: [Validators.required] }),
    mac: this.fb.control(null),
    ips: this.fb.control<Ipv4[]>([]),
    pdoMode: this.fb.control<SpoePdoMode>(null),
    noOdCache: this.fb.control(false),
  });

  connectForm = this.fb.group({
    systemAliveTimeout: this.fb.control(null, { validators: [Validators.required] }),
    clientId: this.fb.control(null, { validators: [Validators.required] }),
    clientAliveTimeout: this.fb.control(null, { validators: [Validators.required] }),
    hostname: this.fb.control(null, { validators: [Validators.required] }),
    pingSystemInterval: this.fb.control(null, { validators: [Validators.required] }),
    pubSubPort: this.fb.control(null, { validators: [Validators.required] }),
    remember: this.fb.control(null, { validators: [Validators.required] }),
    reqResPort: this.fb.control(null, { validators: [Validators.required] }),
  });

  connecting$ = new BehaviorSubject<boolean>(false);
  starting$ = new BehaviorSubject<boolean>(false);
  clearingOdCache$ = new BehaviorSubject<boolean>(false);
  stopping$ = new BehaviorSubject<boolean>(false);

  readonly fieldBusMap = new Map<string, { title: string; logoUrl: string; description: string }>([
    [
      'ethercat',
      {
        title: 'EtherCAT',
        logoUrl: 'assets/images/fieldBuses/ethercat.svg',
        description:
          'EtherCAT® is a registered trademark and patented technology, licensed by Beckhoff Automation GmbH, Germany.',
      },
    ],
    ['ethernetip', { title: 'EtherNet/IP™', logoUrl: '', description: 'EtherNet/IP™ is a trademark of ODVA, Inc.' }],
    [
      'profinet',
      {
        title: 'PROFINET®',
        logoUrl: '',
        description: 'PROFINET® is a registered trademark of PROFIBUS Nutzerorganisation e.V. (PNO).',
      },
    ],
  ]);

  /**
   * Network interfaces fetched from the Electron app.
   */
  networkInterfaces?: NetworkInterface[];

  get isFieldBusEtherCat() {
    return this.startForm.get('fieldBus')?.value === 'ethercat';
  }

  get isPdoModeControl() {
    return this.startForm.get('pdoMode')?.value === 'control';
  }

  get isSoem() {
    return this.isFieldBusEtherCat && this.isPdoModeControl && this.startForm.get('spoeCheckbox')?.value === false;
  }

  get driver() {
    const isSetFieldBusAndPdoMode = this.startForm.get('fieldBus')?.value && this.startForm.get('pdoMode')?.value;
    const driver = isSetFieldBusAndPdoMode ? (this.isSoem ? 'soem' : 'spoe') : null;
    this.startForm.get('driver')?.setValue(driver, { emitEvent: false });
    return driver;
  }

  get startFormInvalid() {
    if (this.driver === 'soem') {
      return this.startForm.get('mac')?.invalid;
    } else if (this.driver === 'spoe') {
      return this.startForm.get('ips')?.invalid || (this.startForm.get('ips')?.value?.length ?? 0) < 1;
    }
    return this.driver === null;
  }

  get isSpoeDriver() {
    return this.driver === 'spoe' || (this.driver === 'soem' && this.startForm.get('spoeCheckbox')?.value === true);
  }

  get ports() {
    const reqResPort = this.startForm?.get('reqResPort')?.value;
    const pubSubPort = this.startForm?.get('pubSubPort')?.value;
    return { reqResPort, pubSubPort };
  }

  showSpoeInstructions = false;

  get showNpcapWarning() {
    return this.isElectronApp ? this.platform === 'win32' && !this.isNpcapInstalled && this.isSoem : false;
  }

  initializeStartForm() {
    const startOptions = getCurrentValue(this.globalService.startOptions$);
    this.startForm.patchValue({
      fieldBus: startOptions?.fieldBus,
      spoeCheckbox: startOptions?.spoeCheckbox ?? false,
      reqResPort: startOptions?.reqResPort ?? 63524,
      pubSubPort: startOptions?.pubSubPort ?? 63525,
      driver: startOptions?.driver,
      mac: startOptions?.mac,
      ips: (startOptions?.ips as Ipv4[]) ?? [],
      pdoMode: startOptions?.pdoMode as SpoePdoMode,
      noOdCache: startOptions?.noOdCache ?? false,
    });
  }

  initializeConnectForm() {
    const connectionConfig = getCurrentValue(this.settingService.connectionConfig$);
    this.connectForm.setValue({
      systemAliveTimeout: connectionConfig?.systemAliveTimeout ?? 3000,
      clientId: generateClientId(),
      clientAliveTimeout: connectionConfig?.clientAliveTimeout ?? 9000,
      hostname: connectionConfig?.hostname ?? (window.location.hostname || 'localhost'),
      pingSystemInterval: connectionConfig?.pingSystemInterval ?? 500,
      pubSubPort: connectionConfig?.pubSubPort ?? this.ports.pubSubPort ?? 63525,
      reqResPort: connectionConfig?.reqResPort ?? this.ports.reqResPort ?? 63524,
      remember: connectionConfig?.remember ?? true,
    });
  }

  setConnectionPortsFromStartPorts() {
    const startFormValues = this.startForm.value;

    this.connectForm.patchValue({
      reqResPort: startFormValues.reqResPort,
      pubSubPort: startFormValues.pubSubPort,
    });
  }

  async configureElectronApp() {
    this.platform = await this.electronService.getPlatform();
    this.isNpcapInstalled = await this.electronService.isNpcapInstalled();
    this.isPortInUse =
      (await this.electronService.isPortInUse(this.ports.reqResPort)) &&
      getCurrentValue(this.motionMasterRunning$) === false;
    this.networkInterfaces = await this.electronService.getNetworkInterfaces();

    if (!this.networkInterfaces?.length) {
      this.toastr.error(
        'Wired network adapters are not found on your computer. Consequently, you cannot run the master process without one. As a result, you will only be able to connect to a remote master process.',
        'Network interface error',
        { disableTimeOut: true },
      );
    } else {
      // Preserve a previously saved NIC selection if it still exists; otherwise fall back to the first interface.
      // The MAC can also be typed by hand in expert mode, so match case-insensitively and store it exactly as
      // the interface reports it, which keeps the matching <option> selected.
      const currentMac = this.startForm.get('mac')?.value;
      const savedInterface = this.networkInterfaces.find((ni) => ni.mac.toLowerCase() === currentMac?.toLowerCase());
      const mac = (savedInterface ?? this.networkInterfaces[0]).mac;
      if (mac !== currentMac) {
        this.startForm.patchValue({ mac });
      }
    }
  }

  async ngOnInit() {
    this.isElectronApp = isElectronContext();
    const connectionConfig = getCurrentValue(this.settingService.connectionConfig$);

    if (this.isElectronApp) {
      this.initializeStartForm();
      await this.configureElectronApp();
    }

    this.initializeConnectForm();

    if (this.isElectronApp) {
      if (getCurrentValue(this.motionMasterRunning$) && connectionConfig?.remember) {
        this.onConnectSubmit();
      }
    } else if (connectionConfig?.remember) {
      this.onConnectSubmit();
    }

    // Handle WebSocket connection close.
    this.cn.client.reqResSocket.close$.subscribe((ev) => {
      if (ev.code !== 1005) {
        // Motion Master does not provide the close code, therefore 1005 is considered a normal closure.
        const message = `Failed to connect to ${this.cn.client.reqResSocket.url}. Is the address correct?`;
        this.toastr.error(message, undefined, { disableTimeOut: true });
        this.settingService.updateSettingState({ connectionConfig: undefined });
        this.connecting$.next(false);
      }
    });
  }

  onChangeNetworkInterface(event: Event) {
    const selectedValue = (event.target as HTMLSelectElement).value;
    this.startForm.get('mac')?.setValue(selectedValue);
  }

  async onStartSubmit() {
    if (!this.startForm.valid) {
      return;
    }

    const isExpertMode = getCurrentValue(this.settingService.expertMode$);

    this.starting$.next(true);

    const startOptions = this.startForm.value as Partial<SystemInformation['startOptions']>;
    const started = await this.electronService.startMotionMaster(startOptions);

    if (started) {
      this.globalService.updateStartOptions(startOptions);
      this.setConnectionPortsFromStartPorts();
      if (!isExpertMode) {
        this.onConnectSubmit();
      }
    } else {
      const message =
        this.platform === 'win32'
          ? 'Failed to start the master process. Please check if Npcap is installed in WinPcap API-compatible Mode'
          : 'Failed to start the master process.';
      this.toastr.error(message, '', { disableTimeOut: true });
    }

    this.starting$.next(false);
  }

  canClearOdCache = this.electronService.canClearOdCache;

  async onClearOdCache() {
    this.clearingOdCache$.next(true);
    const { success, output } = await this.electronService.clearOdCache();
    const summary = output
      .split('\n')
      .map((line) => line.replace(/^.*\|\s*/, '').trim())
      .filter((line) => line.startsWith('Deleted ') && line.includes(' cached object dictionaries '))
      .pop();
    if (success) {
      this.toastr.success(summary ?? 'The object dictionary cache is cleared.');
    } else {
      this.toastr.error(
        summary || output.trim() || 'Failed to clear the object dictionary cache.',
        'Failed to clear the object dictionary cache',
        { disableTimeOut: true },
      );
    }
    this.clearingOdCache$.next(false);
  }

  onSaveTheMasterProcessStandardOutput() {
    this.electronService.getMotionMasterStdout().then((lines) => {
      const blob = new Blob([lines?.join('') ?? 'The standard output is empty.'], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, 'master-process-stdout.txt');
    });
  }

  async onStop() {
    this.stopping$.next(true);
    const stopped = await this.globalService.stopMotionMaster();
    this.stopping$.next(false);

    if (stopped) {
      this.isPortInUse = await this.electronService.isPortInUse(this.ports.reqResPort);
    }
  }

  onConnectSubmit() {
    if (!this.connectForm.valid) {
      return;
    }

    const connectionConfig = this.connectForm.value as MotionMasterConnectionConfig;
    const expertMode = getCurrentValue(this.settingService.expertMode$);

    this.globalService.dispatchConnectedWithExpertMode(expertMode);
    this.settingService.updateSettingState({ connectionConfig });

    this.connecting$.next(true);
    this.cn.connect(connectionConfig);
  }
}
